import React, { useState, useRef, useEffect } from 'react';
import { Box, Grid, Heading } from '@theme-ui/components';
import { Container, Markdown } from '~/Common';
import PlusMinusIcon from './PlusMinusIcon';

export const FAQ = ({ data, container, ...props }) => {
  const title = data?.title ?? null;
  const description = data?.description ?? null;
  const questions_and_answers = data?.questions_and_answers ?? null;

  const [activeIndex, setActiveIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [heights, setHeights] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const contentRefs = useRef([]);
  const iconRef = useRef(null);
  const faqsContainerRef = useRef(null);
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0; 

  useEffect(() => {
    const calculatedHeights = contentRefs.current.map(ref => ref.scrollHeight);
    setHeights(calculatedHeights);
  }, [questions_and_answers]);

  const handleToggle = (index) => {
    setActiveIndex(index === activeIndex ? null : index);
  };

  const handleMouseEnter = (index) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const handleMouseMove = (e) => {
    setMousePosition({
      x: e.clientX,
      y: e.clientY,
    });
  };

  useEffect(() => {
    if (iconRef.current) {
      iconRef.current.style.left = `${mousePosition.x - faqsContainerRef.current.getBoundingClientRect().x - 35}px`;
      iconRef.current.style.top = `${mousePosition.y - faqsContainerRef.current.getBoundingClientRect().y - 35}px`;
    }
  }, [mousePosition]);

  return (
    <Box bg="white" {...props}>
      <Container variant="layouts.section" {...container}>
        {title && <Heading as="h2" variant="content_block_heading">{title}</Heading>}
        {description && (
          <Box className="markdown" mb={[4, 4, 5]}>
            <Markdown>{description}</Markdown>
          </Box>
        )}

        {questions_and_answers && (
          <Grid sx={{
                gridGap: 4,
                gridTemplateColumns: ['1fr'],
                width: '100%',
                position: 'relative',
                cursor: 'none',
            }} 
            ref={faqsContainerRef}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {questions_and_answers.map((faq, index) => (
              <Box
                key={index}
                sx={{
                  position: 'relative',
                  paddingTop: index === 0 ? 0 : '5px',
                  overflow: 'hidden',
                }}
                onMouseEnter={() => handleMouseEnter(index)}
                onClick={() => handleToggle(index)}
              >
                <Box
                  onKeyDown={(e) => e.key === 'Enter' && handleToggle(index)}
                  tabIndex="0"
                  role="button"
                  sx={{
                    outline: 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:focus-visible': {
                      outline: '3px solid teal',
                      outlineOffset: '3px',
                    },
                  }}
                >
                  <Heading as="h3" variant="h3" mb={3} sx={{
                    fontSize: '22px',
                    fontWeight: 500,
                  }}>
                    {faq.question}
                  </Heading>
                </Box>
                <Box
                  ref={el => contentRefs.current[index] = el}
                  sx={{
                    maxHeight: activeIndex === index ? `${heights[index]}px` : '0px',
                    overflow: 'hidden',
                    transition: 'max-height 0.6s ease',
                  }}
                >
                  <Box className="markdown" sx={{ lineHeight: '1.5', paddingBottom: '20px' }}>
                    <Markdown>{faq.answer}</Markdown>
                  </Box>
                </Box>
                <Box sx={{
                    width: '100%',
                    height: '1px',
                    background: t => t.colors.teal,
                    transition: 'all 800ms ease-in-out',
                  }}></Box>
              </Box>
            ))}
            <Box
                ref={iconRef}
                sx={{
                    position: 'absolute',
                    width: '70px',
                    height: '70px',
                    backgroundColor: 'teal',
                    borderRadius: '50%',
                    transform: hoveredIndex === null ? 'scale(0)' : 'scale(1)',
                    transition: 'transform 0.6s ease',
                    display: isTouchDevice ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                }}
                >
                <PlusMinusIcon isActive={hoveredIndex === activeIndex} />
            </Box>
          </Grid>
        )}
      </Container>
    </Box>
  );
};
